/**
 * studio-tools.ts
 * Shared studio tool definitions and theme-aware icons for project tools.
 */
import React from "react";

// --- Shared Types ---
export type IconFC = React.FC<{ className?: string; color?: string }>;

interface StudioCard {
	id: string;
	label: string;
	icon: IconFC;
	color: string;
	toolSlug: string | null;
}

// --- Custom Studio Icons ---
export const IconMarketOverview: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_mo)" },
			React.createElement("path", {
				d: "M19.1667 18.3333H4.16667C3.50363 18.3333 2.86774 18.0699 2.3989 17.6011C1.93006 17.1323 1.66667 16.4964 1.66667 15.8333V0.833333C1.66667 0.61232 1.57887 0.400358 1.42259 0.244078C1.26631 0.0877974 1.05435 0 0.833333 0C0.61232 0 0.400358 0.0877974 0.244078 0.244078C0.0877974 0.400358 0 0.61232 0 0.833333L0 15.8333C0.00132321 16.938 0.440735 17.997 1.22185 18.7782C2.00296 19.5593 3.062 19.9987 4.16667 20H19.1667C19.3877 20 19.5996 19.9122 19.7559 19.7559C19.9122 19.5996 20 19.3877 20 19.1667C20 18.9457 19.9122 18.7337 19.7559 18.5774C19.5996 18.4211 19.3877 18.3333 19.1667 18.3333Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M4.9974 16.6641C5.21841 16.6641 5.43037 16.5763 5.58665 16.42C5.74293 16.2637 5.83073 16.0517 5.83073 15.8307V9.9974C5.83073 9.77638 5.74293 9.56442 5.58665 9.40814C5.43037 9.25186 5.21841 9.16406 4.9974 9.16406C4.77638 9.16406 4.56442 9.25186 4.40814 9.40814C4.25186 9.56442 4.16406 9.77638 4.16406 9.9974V15.8307C4.16406 16.0517 4.25186 16.2637 4.40814 16.42C4.56442 16.5763 4.77638 16.6641 4.9974 16.6641Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M8.33594 8.33333V15.8333C8.33594 16.0543 8.42374 16.2663 8.58002 16.4226C8.7363 16.5789 8.94826 16.6667 9.16927 16.6667C9.39028 16.6667 9.60225 16.5789 9.75853 16.4226C9.91481 16.2663 10.0026 16.0543 10.0026 15.8333V8.33333C10.0026 8.11232 9.91481 7.90036 9.75853 7.74408C9.60225 7.5878 9.39028 7.5 9.16927 7.5C8.94826 7.5 8.7363 7.5878 8.58002 7.74408C8.42374 7.90036 8.33594 8.11232 8.33594 8.33333Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M12.5 10.8333V15.8333C12.5 16.0543 12.5878 16.2663 12.7441 16.4226C12.9004 16.5789 13.1123 16.6667 13.3333 16.6667C13.5543 16.6667 13.7663 16.5789 13.9226 16.4226C14.0789 16.2663 14.1667 16.0543 14.1667 15.8333V10.8333C14.1667 10.6123 14.0789 10.4004 13.9226 10.2441C13.7663 10.0878 13.5543 10 13.3333 10C13.1123 10 12.9004 10.0878 12.7441 10.2441C12.5878 10.4004 12.5 10.6123 12.5 10.8333Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M16.6641 7.4974V15.8307C16.6641 16.0517 16.7519 16.2637 16.9081 16.42C17.0644 16.5763 17.2764 16.6641 17.4974 16.6641C17.7184 16.6641 17.9304 16.5763 18.0867 16.42C18.2429 16.2637 18.3307 16.0517 18.3307 15.8307V7.4974C18.3307 7.27638 18.2429 7.06442 18.0867 6.90814C17.9304 6.75186 17.7184 6.66406 17.4974 6.66406C17.2764 6.66406 17.0644 6.75186 16.9081 6.90814C16.7519 7.06442 16.6641 7.27638 16.6641 7.4974Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M4.99722 7.49757C5.21821 7.49753 5.43014 7.4097 5.58638 7.25341L8.57472 4.26507C8.73355 4.11376 8.94451 4.02936 9.16388 4.02936C9.38326 4.02936 9.59422 4.11376 9.75305 4.26507L11.5631 6.07507C12.0319 6.54375 12.6676 6.80704 13.3306 6.80704C13.9935 6.80704 14.6292 6.54375 15.0981 6.07507L19.7531 1.42007C19.9049 1.2629 19.9888 1.0524 19.9869 0.833905C19.985 0.615407 19.8974 0.406397 19.7429 0.25189C19.5884 0.0973835 19.3794 0.00974268 19.1609 0.007844C18.9424 0.00594532 18.7319 0.0899407 18.5747 0.241739L13.9197 4.89591C13.7634 5.05213 13.5515 5.13989 13.3306 5.13989C13.1096 5.13989 12.8977 5.05213 12.7414 4.89591L10.9314 3.08674C10.4626 2.61806 9.8268 2.35477 9.16388 2.35477C8.50097 2.35477 7.8652 2.61806 7.39638 3.08674L4.40805 6.07507C4.29154 6.19162 4.2122 6.34009 4.18007 6.50171C4.14793 6.66334 4.16443 6.83087 4.22749 6.98312C4.29055 7.13537 4.39733 7.2655 4.53434 7.35707C4.67134 7.44864 4.83243 7.49754 4.99722 7.49757Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_mo" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconTargetPersonas: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_tp)" },
			React.createElement("path", {
				d: "M6.25 10.8333C5.50832 10.8333 4.7833 10.6134 4.16661 10.2013C3.54993 9.78929 3.06928 9.20362 2.78545 8.5184C2.50162 7.83318 2.42736 7.07918 2.57206 6.35175C2.71675 5.62432 3.0739 4.95613 3.59835 4.43168C4.1228 3.90724 4.79098 3.55008 5.51841 3.40539C6.24584 3.2607 6.99984 3.33496 7.68506 3.61879C8.37029 3.90262 8.95596 4.38326 9.36801 4.99995C9.78007 5.61663 10 6.34166 10 7.08334C9.9989 8.07756 9.60345 9.03075 8.90043 9.73377C8.19741 10.4368 7.24422 10.8322 6.25 10.8333ZM6.25 5C5.83796 5 5.43516 5.12219 5.09256 5.35111C4.74996 5.58003 4.48293 5.9054 4.32525 6.28608C4.16757 6.66676 4.12631 7.08565 4.2067 7.48977C4.28708 7.8939 4.4855 8.26511 4.77686 8.55647C5.06822 8.84783 5.43943 9.04625 5.84356 9.12664C6.24769 9.20702 6.66658 9.16577 7.04726 9.00808C7.42794 8.8504 7.75331 8.58338 7.98223 8.24077C8.21115 7.89817 8.33333 7.49538 8.33333 7.08334C8.33333 6.5308 8.11384 6.0009 7.72314 5.6102C7.33244 5.2195 6.80253 5 6.25 5ZM12.5 16.6667C12.4987 15.562 12.0593 14.503 11.2781 13.7219C10.497 12.9407 9.438 12.5013 8.33333 12.5H4.16667C3.062 12.5013 2.00296 12.9407 1.22185 13.7219C0.440735 14.503 0.00132321 15.562 0 16.6667L0 20H1.66667V16.6667C1.66667 16.0036 1.93006 15.3677 2.3989 14.8989C2.86774 14.4301 3.50363 14.1667 4.16667 14.1667H8.33333C8.99637 14.1667 9.63226 14.4301 10.1011 14.8989C10.5699 15.3677 10.8333 16.0036 10.8333 16.6667V20H12.5V16.6667ZM14.5833 7.5C13.8417 7.5 13.1166 7.28007 12.4999 6.86801C11.8833 6.45596 11.4026 5.87029 11.1188 5.18506C10.835 4.49984 10.7607 3.74584 10.9054 3.01841C11.0501 2.29098 11.4072 1.6228 11.9317 1.09835C12.4561 0.573904 13.1243 0.216751 13.8517 0.0720569C14.5792 -0.0726377 15.3332 0.00162482 16.0184 0.285453C16.7036 0.569282 17.2893 1.04993 17.7013 1.66661C18.1134 2.2833 18.3333 3.00832 18.3333 3.75C18.3322 4.74423 17.9368 5.69741 17.2338 6.40043C16.5307 7.10346 15.5776 7.4989 14.5833 7.5ZM14.5833 1.66667C14.1713 1.66667 13.7685 1.78885 13.4259 2.01777C13.0833 2.24669 12.8163 2.57207 12.6586 2.95274C12.5009 3.33342 12.4596 3.75231 12.54 4.15644C12.6204 4.56057 12.8188 4.93178 13.1102 5.22314C13.4016 5.5145 13.7728 5.71292 14.1769 5.7933C14.581 5.87369 14.9999 5.83243 15.3806 5.67475C15.7613 5.51707 16.0866 5.25004 16.3156 4.90744C16.5445 4.56484 16.6667 4.16205 16.6667 3.75C16.6667 3.19747 16.4472 2.66756 16.0565 2.27686C15.6658 1.88616 15.1359 1.66667 14.5833 1.66667ZM20 13.3333C19.9987 12.2287 19.5593 11.1696 18.7782 10.3885C17.997 9.6074 16.938 9.16799 15.8333 9.16667H12.5V10.8333H15.8333C16.4964 10.8333 17.1323 11.0967 17.6011 11.5656C18.0699 12.0344 18.3333 12.6703 18.3333 13.3333V16.6667H20V13.3333Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_tp" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconCompetitors: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_comp)" },
			React.createElement("path", {
				d: "M16.0798 8.67167L17.8407 6.91083C18.1665 6.585 18.6932 6.585 19.019 6.91083C19.3448 7.23667 19.3448 7.76333 19.019 8.08917L17.149 9.95917C16.8632 10.245 16.4832 10.4025 16.079 10.4025H16.0707C15.664 10.4008 15.2823 10.2392 14.9973 9.94833L13.989 8.92C13.6665 8.59083 13.6715 8.06417 14.0007 7.74167C14.3298 7.41833 14.8565 7.425 15.179 7.75333L16.0807 8.6725L16.0798 8.67167ZM1.91149 8.08917C2.07399 8.25167 2.28732 8.33333 2.50066 8.33333C2.71399 8.33333 2.92732 8.25167 3.08982 8.08917L3.75066 7.42833L4.41149 8.08917C4.57399 8.25167 4.78732 8.33333 5.00066 8.33333C5.21399 8.33333 5.42732 8.25167 5.58982 8.08917C5.91566 7.76333 5.91566 7.23667 5.58982 6.91083L4.92899 6.25L5.58982 5.58917C5.91566 5.26333 5.91566 4.73667 5.58982 4.41083C5.26399 4.085 4.73732 4.085 4.41149 4.41083L3.75066 5.07167L3.08982 4.41083C2.76399 4.085 2.23732 4.085 1.91149 4.41083C1.58566 4.73667 1.58566 5.26333 1.91149 5.58917L2.57232 6.25L1.91149 6.91083C1.58566 7.23667 1.58566 7.76333 1.91149 8.08917ZM3.75066 14.1667C1.88399 14.1667 0.267324 12.695 0.000657829 10.8633C-0.00350884 10.7425 -0.00184213 10.1175 0.458991 9.64C0.667324 9.42417 1.04232 9.16667 1.64566 9.16667H5.85482C6.45816 9.16667 6.83316 9.42417 7.04149 9.64C7.50232 10.1175 7.50399 10.7433 7.49982 10.8633C7.23649 12.6958 5.62066 14.1667 3.75066 14.1667ZM3.75066 12.5C4.75482 12.5 5.62316 11.7933 5.82066 10.8367L1.67982 10.8333C1.87816 11.7917 2.74732 12.5 3.75066 12.5ZM20.0007 13.3633C19.7373 15.1958 18.1207 16.6667 16.2507 16.6667C14.3807 16.6667 12.7673 15.195 12.5007 13.3633C12.4965 13.2425 12.4982 12.6175 12.959 12.14C13.1673 11.9242 13.5423 11.6667 14.1457 11.6667H18.3548C18.9582 11.6667 19.3332 11.9242 19.5415 12.14C20.0023 12.6175 20.0048 13.2433 20.0007 13.3633ZM18.3215 13.3367L14.1807 13.3333C14.379 14.2917 15.2482 15 16.2515 15C17.2548 15 18.124 14.2933 18.3215 13.3367ZM15.8348 18.3333H10.8348V4.40083L16.4657 5.80833C16.534 5.82583 16.6015 5.83333 16.669 5.83333C17.0423 5.83333 17.3815 5.58083 17.4765 5.2025C17.5882 4.75583 17.3165 4.30333 16.8698 4.19167L10.834 2.6825V0.833333C10.834 0.373333 10.4607 0 10.0007 0C9.54066 0 9.16732 0.373333 9.16732 0.833333V2.26583L3.53649 0.858333C3.09066 0.745833 2.63732 1.01833 2.52566 1.465C2.41399 1.91167 2.68566 2.36417 3.13232 2.47583L9.16816 3.985V18.3342H4.16816C3.70816 18.3342 3.33482 18.7075 3.33482 19.1675C3.33482 19.6275 3.70816 20.0008 4.16816 20.0008H15.8348C16.2948 20.0008 16.6682 19.6275 16.6682 19.1675C16.6682 18.7075 16.2948 18.3333 15.8348 18.3333Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_comp" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconOffer: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_offer)" },
			React.createElement("path", {
				d: "M10 0C4.48583 0 0 4.48583 0 10C0 15.5142 4.48583 20 10 20C15.5142 20 20 15.5142 20 10C20 4.48583 15.5142 0 10 0ZM10 18.3333C5.405 18.3333 1.66667 14.595 1.66667 10C1.66667 5.405 5.405 1.66667 10 1.66667C14.595 1.66667 18.3333 5.405 18.3333 10C18.3333 14.595 14.595 18.3333 10 18.3333ZM13.3333 11.6667C13.3333 13.045 12.2117 14.1667 10.8333 14.1667V15C10.8333 15.4608 10.4608 15.8333 10 15.8333C9.53917 15.8333 9.16667 15.4608 9.16667 15V14.1667H8.94333C8.05417 14.1667 7.22417 13.6883 6.77833 12.9175C6.5475 12.5183 6.68417 12.0092 7.08167 11.7792C7.48083 11.5467 7.99083 11.685 8.22 12.0825C8.36917 12.3408 8.64583 12.5 8.9425 12.5H10.8325C11.2925 12.5 11.6658 12.1267 11.6658 11.6667C11.6658 11.3517 11.44 11.085 11.1292 11.0333L8.595 10.6108C7.47667 10.425 6.66583 9.46667 6.66583 8.33333C6.66583 6.955 7.7875 5.83333 9.16583 5.83333V5C9.16583 4.54 9.53833 4.16667 9.99917 4.16667C10.46 4.16667 10.8325 4.54 10.8325 5V5.83333H11.0558C11.945 5.83333 12.775 6.3125 13.2208 7.08333C13.4517 7.48167 13.315 7.99083 12.9175 8.22167C12.5175 8.4525 12.0083 8.31583 11.7792 7.9175C11.63 7.66 11.3533 7.50083 11.0567 7.50083H9.16667C8.70667 7.50083 8.33333 7.875 8.33333 8.33417C8.33333 8.64917 8.55917 8.91583 8.87 8.9675L11.4042 9.39C12.5225 9.57583 13.3333 10.5342 13.3333 11.6675V11.6667Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_offer" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconCulturalAdaptation: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_ca)" },
			React.createElement("path", {
				d: "M12.5262 17.0354C10.0978 15.6437 7.50283 13.0646 7.50283 9.99956C7.50283 6.93456 10.0962 4.35706 12.5237 2.9654C13.1337 2.6154 13.4603 1.92873 13.2978 1.26373C13.0195 0.12623 11.7862 0.0387302 11.7862 0.0387302C6.34866 -0.389603 1.53033 2.77456 0.31283 7.5629C-0.438836 10.5187 0.174497 13.4687 2.04033 15.8679C4.052 18.4554 7.297 19.9996 10.7195 19.9996C11.1045 19.9996 11.4753 19.9871 11.8395 19.9571C12.5628 19.8962 13.1495 19.4004 13.2995 18.7229C13.4487 18.0521 13.1445 17.3896 12.5262 17.0354ZM10.7195 18.3337C7.807 18.3337 5.0545 17.0296 3.35616 14.8446C1.81283 12.8604 1.30533 10.4196 1.927 7.97373C2.882 4.2204 6.54116 1.66623 10.8037 1.66623C11.0062 1.66623 11.2103 1.67206 11.4153 1.68373C8.667 3.34623 5.83616 6.3479 5.83616 9.99956C5.83616 13.6512 8.66283 16.6479 11.4162 18.3162C11.1895 18.3287 10.957 18.3337 10.7195 18.3337ZM19.7553 9.5804L18.1128 10.9187L18.792 12.9887C18.8853 13.2687 18.7903 13.5771 18.557 13.7571C18.3228 13.9371 18.0012 13.9496 17.7537 13.7887L15.8412 12.5429L13.9603 13.8012C13.8437 13.8796 13.7087 13.9187 13.5745 13.9187C13.4278 13.9187 13.2812 13.8721 13.1578 13.7804C12.922 13.6037 12.8237 13.2971 12.912 13.0162L13.5653 10.9221L11.9145 9.57873C11.692 9.3904 11.6112 9.08373 11.712 8.8104C11.812 8.53706 12.0728 8.3554 12.3637 8.3554H14.4478L15.1853 6.28623C15.2862 6.01456 15.5462 5.83456 15.8362 5.83456C16.1262 5.83456 16.3862 6.01456 16.487 6.28623L17.2245 8.3554H19.3087C19.6003 8.3554 19.8612 8.5379 19.9612 8.81206C20.0612 9.08623 19.9795 9.39373 19.7562 9.58123L19.7553 9.5804Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_ca" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconChannelsOverview: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_cho)" },
			React.createElement("path", {
				d: "M14.3015 10.8333C14.6632 12.0342 15.7657 12.9167 17.0823 12.9167C18.6907 12.9167 19.999 11.6083 19.999 10C19.999 8.39167 18.6907 7.08333 17.0823 7.08333C15.7657 7.08333 14.6632 7.96583 14.3015 9.16667H13.3323V5C13.3323 4.40417 13.7523 3.90583 14.3115 3.78167C14.6823 4.96583 15.7773 5.83333 17.0823 5.83333C18.6907 5.83333 19.999 4.525 19.999 2.91667C19.999 1.30833 18.6907 0 17.0823 0C15.7548 0 14.644 0.896667 14.2923 2.1125C12.8215 2.26083 11.6657 3.49083 11.6657 5V9.16667H10.8323C10.3715 9.16667 9.999 9.53917 9.999 10C9.999 10.4608 10.3715 10.8333 10.8323 10.8333H11.6657V15C11.6657 16.5092 12.8223 17.7392 14.2923 17.8875C14.644 19.1033 15.7548 20 17.0823 20C18.6907 20 19.999 18.6917 19.999 17.0833C19.999 15.475 18.6907 14.1667 17.0823 14.1667C15.7773 14.1667 14.6823 15.0342 14.3115 16.2183C13.7523 16.0942 13.3323 15.5958 13.3323 15V10.8333H14.3015ZM17.0823 8.75C17.7715 8.75 18.3323 9.31083 18.3323 10C18.3323 10.6892 17.7715 11.25 17.0823 11.25C16.3932 11.25 15.8323 10.6892 15.8323 10C15.8323 9.31083 16.3932 8.75 17.0823 8.75ZM17.0823 1.66667C17.7715 1.66667 18.3323 2.2275 18.3323 2.91667C18.3323 3.60583 17.7715 4.16667 17.0823 4.16667C16.3932 4.16667 15.8323 3.60583 15.8323 2.91667C15.8323 2.2275 16.3932 1.66667 17.0823 1.66667ZM17.0823 15.8333C17.7715 15.8333 18.3323 16.3942 18.3323 17.0833C18.3323 17.7725 17.7715 18.3333 17.0823 18.3333C16.3932 18.3333 15.8323 17.7725 15.8323 17.0833C15.8323 16.3942 16.3932 15.8333 17.0823 15.8333ZM9.69234 7.29667C9.464 6.8975 8.95317 6.76 8.55484 6.99083L7.74067 7.46C7.2365 6.91667 6.57984 6.52167 5.83317 6.35083V5.41667C5.83317 4.95583 5.46067 4.58333 4.99984 4.58333C4.539 4.58333 4.1665 4.95583 4.1665 5.41667V6.35083C3.41984 6.52167 2.76234 6.91583 2.259 7.46L1.44484 6.99083C1.04567 6.76083 0.536504 6.89833 0.307337 7.29667C0.0773373 7.69583 0.214837 8.205 0.613171 8.43417L1.43234 8.90583C1.32484 9.25417 1.25067 9.61667 1.25067 10C1.25067 10.3833 1.32484 10.7458 1.43234 11.0942L0.613171 11.5658C0.214004 11.795 0.0765039 12.305 0.307337 12.7033C0.461504 12.9708 0.741504 13.1208 1.02984 13.1208C1.17067 13.1208 1.314 13.085 1.44484 13.0092L2.259 12.54C2.76317 13.0833 3.41984 13.4783 4.1665 13.6492V14.5833C4.1665 15.0442 4.539 15.4167 4.99984 15.4167C5.46067 15.4167 5.83317 15.0442 5.83317 14.5833V13.6492C6.57984 13.4783 7.23734 13.0842 7.74067 12.54L8.55484 13.0092C8.68567 13.085 8.829 13.1208 8.96984 13.1208C9.25817 13.1208 9.539 12.9708 9.69234 12.7033C9.92234 12.3042 9.78484 11.795 9.3865 11.5658L8.56734 11.0942C8.67484 10.7458 8.749 10.3833 8.749 10C8.749 9.61667 8.67484 9.25417 8.56734 8.90583L9.3865 8.43417C9.78567 8.205 9.92317 7.695 9.69234 7.29667ZM4.999 12.0833C3.84984 12.0833 2.91567 11.1492 2.91567 10C2.91567 8.85083 3.84984 7.91667 4.999 7.91667C6.14817 7.91667 7.08234 8.85083 7.08234 10C7.08234 11.1492 6.14817 12.0833 4.999 12.0833Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_cho" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconMediaPlan: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_mp)" },
			React.createElement("path", {
				d: "M13.2767 1.38335C12.99 0.572516 11.7792 0.731683 11.6717 1.57085C11.5217 2.91668 10.9633 4.16252 7.91667 4.16252H3.33333C1.495 4.16335 0 5.65835 0 7.49668V9.16335C0 10.4375 0.726667 11.5333 1.77917 12.0942L4.23 17.6167C4.64667 18.5558 5.57917 19.1625 6.6075 19.1625C7.4875 19.1625 8.30083 18.7233 8.7825 17.9867C9.26417 17.25 9.3425 16.33 8.99083 15.5225L7.6725 12.495H7.91583C10.9625 12.495 11.5208 13.7408 11.6708 15.0867C11.8017 16.0992 13.3075 16.0092 13.3325 14.995V1.66335C13.315 1.57002 13.3025 1.47502 13.2758 1.38335H13.2767ZM7.46333 16.1892C7.59167 16.4833 7.56417 16.8058 7.38833 17.0742C7.01167 17.6517 6.03417 17.5725 5.75417 16.9408L3.78167 12.4967H5.855L7.46333 16.1892ZM7.91667 10.8292H3.33333C2.41417 10.8292 1.66667 10.0817 1.66667 9.16252V7.49585C1.66667 6.57668 2.41417 5.82918 3.33333 5.82918H7.91667C9.51917 5.82918 10.7675 5.49835 11.6667 4.85168V11.8075C10.7675 11.1608 9.51917 10.83 7.91667 10.83V10.8292ZM18.0475 13.0433C17.885 13.2058 17.6717 13.2875 17.4583 13.2875C17.245 13.2875 17.0317 13.2058 16.8692 13.0433C16.5433 12.7175 16.5433 12.1908 16.8692 11.865C18.8183 9.91585 18.8183 6.74335 16.8692 4.79418C16.5433 4.46835 16.5433 3.94168 16.8692 3.61585C17.195 3.29002 17.7217 3.29002 18.0475 3.61585C20.6467 6.21502 20.6467 10.4442 18.0475 13.0433ZM15.5975 10.93C15.435 11.0925 15.2217 11.1742 15.0083 11.1742C14.795 11.1742 14.5817 11.0925 14.4192 10.93C14.0933 10.6042 14.0933 10.0775 14.4192 9.75168C15.2033 8.96752 15.2033 7.69085 14.4192 6.90668C14.0933 6.58085 14.0933 6.05418 14.4192 5.72835C14.745 5.40252 15.2717 5.40252 15.5975 5.72835C17.0317 7.16252 17.0317 9.49585 15.5975 10.93Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_mp" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconContentPlan: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_cp)" },
			React.createElement("path", {
				d: "M14.1667 13.3333C13.7067 13.3333 13.3333 13.7067 13.3333 14.1667V15H11.6667V14.1667C11.6667 13.7067 11.2933 13.3333 10.8333 13.3333C10.3733 13.3333 10 13.7067 10 14.1667V18.3333H5V9.16667C5 8.70667 4.62667 8.33333 4.16667 8.33333H3.75C1.6825 8.33333 0 10.0158 0 12.0833V16.25C0 18.3175 1.6825 20 3.75 20H11.25C13.3175 20 15 18.3175 15 16.25V14.1667C15 13.7067 14.6267 13.3333 14.1667 13.3333ZM3.33333 13.3333V15H1.66667V13.3333H3.33333ZM3.33333 10.0417V11.6667H1.7125C1.87917 10.8525 2.51917 10.2083 3.33333 10.0417ZM1.7125 16.6667H3.33333V18.2917C2.51833 18.1258 1.87917 17.4817 1.7125 16.6667ZM11.6667 18.2908V16.6667H13.2908C13.125 17.4817 12.4817 18.125 11.6667 18.2908ZM15.8333 0H10.8333C8.53583 0 6.66667 1.86917 6.66667 4.16667V7.5C6.66667 9.7975 8.53583 11.6667 10.8333 11.6667H15.8333C18.1308 11.6667 20 9.7975 20 7.5V4.16667C20 1.86917 18.1308 0 15.8333 0ZM8.33333 4.16667C8.33333 2.78833 9.455 1.66667 10.8333 1.66667H15.8333C16.7892 1.66667 17.6117 2.21167 18.0317 3.00167L15.1508 5.88833L14.755 5.48833C14.1417 4.87417 13.175 4.83083 12.5008 5.39417L8.65 8.695C8.45417 8.33833 8.3325 7.93583 8.3325 7.50083V4.16667H8.33333ZM15.8333 10H10.8333C10.5042 10 10.1908 9.9325 9.9025 9.81583L13.5742 6.6625L13.97 7.0625C14.6192 7.7125 15.6775 7.71333 16.3275 7.0625L18.3342 5.05583V7.49917C18.3342 8.8775 17.2125 9.99917 15.8342 9.99917L15.8333 10ZM9.16667 3.75C9.16667 3.06 9.72667 2.5 10.4167 2.5C11.1067 2.5 11.6667 3.06 11.6667 3.75C11.6667 4.44 11.1067 5 10.4167 5C9.72667 5 9.16667 4.44 9.16667 3.75Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_cp" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconInstagramAds: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_ig)" },
			React.createElement("path", {
				d: "M19.9766 5.88005C19.9297 4.81738 19.7579 4.0868 19.5117 3.45374C19.2577 2.78176 18.867 2.18014 18.3551 1.68002C17.855 1.1721 17.2494 0.777435 16.5852 0.527447C15.9485 0.281274 15.2217 0.109427 14.1591 0.0625732C13.0885 0.0117516 12.7486 0 10.0332 0C7.31783 0 6.97795 0.0117516 5.9113 0.0586052C4.84863 0.105459 4.11805 0.277459 3.48514 0.523479C2.81301 0.777435 2.21139 1.16814 1.71127 1.68002C1.20335 2.18014 0.808837 2.78573 0.558697 3.44992C0.312524 4.0868 0.140677 4.81341 0.0938232 5.87609C0.0430016 6.9467 0.03125 7.28658 0.03125 10.002C0.03125 12.7173 0.0430016 13.0572 0.0898552 14.1239C0.136709 15.1865 0.308709 15.9171 0.554881 16.5502C0.808837 17.2221 1.20335 17.8238 1.71127 18.3239C2.21139 18.8318 2.81698 19.2265 3.48117 19.4765C4.11805 19.7226 4.84466 19.8945 5.90749 19.9413C6.97398 19.9883 7.31401 19.9999 10.0294 19.9999C12.7448 19.9999 13.0846 19.9883 14.1513 19.9413C15.214 19.8945 15.9445 19.7226 16.5775 19.4765C17.9216 18.9568 18.9842 17.8941 19.5039 16.5502C19.7499 15.9133 19.9219 15.1865 19.9688 14.1239C20.0156 13.0572 20.0274 12.7173 20.0274 10.002C20.0274 7.28658 20.0234 6.9467 19.9766 5.88005ZM18.1755 14.0457C18.1325 15.0225 17.9684 15.5499 17.8317 15.9015C17.4956 16.7728 16.8041 17.4643 15.9328 17.8004C15.5812 17.9372 15.0499 18.1012 14.077 18.1441C13.0221 18.1911 12.7057 18.2027 10.0372 18.2027C7.36865 18.2027 7.04831 18.1911 5.99723 18.1441C5.02048 18.1012 4.49303 17.9372 4.1414 17.8004C3.70781 17.6402 3.31314 17.3862 2.9928 17.0541C2.6607 16.7298 2.40674 16.3391 2.2465 15.9055C2.10975 15.5539 1.94569 15.0225 1.9028 14.0497C1.85579 12.9948 1.8442 12.6783 1.8442 10.0097C1.8442 7.34122 1.85579 7.02087 1.9028 5.96995C1.94569 4.99319 2.10975 4.46575 2.2465 4.11412C2.40674 3.68038 2.6607 3.28586 2.99676 2.96536C3.32092 2.63327 3.71163 2.37931 4.14537 2.21921C4.497 2.08247 5.02841 1.9184 6.0012 1.87537C7.05609 1.82851 7.37262 1.81676 10.041 1.81676C12.7135 1.81676 13.0299 1.82851 14.0809 1.87537C15.0577 1.9184 15.5851 2.08247 15.9368 2.21921C16.3703 2.37931 16.765 2.63327 17.0854 2.96536C17.4175 3.28967 17.6714 3.68038 17.8317 4.11412C17.9684 4.46575 18.1325 4.99701 18.1755 5.96995C18.2224 7.02484 18.2341 7.34122 18.2341 10.0097C18.2341 12.6783 18.2224 12.9908 18.1755 14.0457Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M10.0362 4.86719C7.19976 4.86719 4.89844 7.16836 4.89844 10.0049C4.89844 12.8415 7.19976 15.1426 10.0362 15.1426C12.8727 15.1426 15.1739 12.8415 15.1739 10.0049C15.1739 7.16836 12.8727 4.86719 10.0362 4.86719ZM10.0362 13.3376C8.19605 13.3376 6.70345 11.8452 6.70345 10.0049C6.70345 8.16465 8.19605 6.6722 10.0362 6.6722C11.8764 6.6722 13.3689 8.16465 13.3689 10.0049C13.3689 11.8452 11.8764 13.3376 10.0362 13.3376Z",
				fill: color,
			}),
			React.createElement("path", {
				d: "M16.5787 4.66036C16.5787 5.32272 16.0416 5.85978 15.3791 5.85978C14.7167 5.85978 14.1797 5.32272 14.1797 4.66036C14.1797 3.99785 14.7167 3.46094 15.3791 3.46094C16.0416 3.46094 16.5787 3.99785 16.5787 4.66036Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_ig" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

export const IconGoogleAds: IconFC = ({ className, color = "currentColor" }) =>
	React.createElement(
		"svg",
		{
			className,
			width: "20",
			height: "20",
			viewBox: "0 0 20 20",
			fill: "none",
			xmlns: "http://www.w3.org/2000/svg",
		},
		React.createElement(
			"g",
			{ clipPath: "url(#clip_ga)" },
			React.createElement("path", {
				d: "M19.5051 13.616L13.2149 2.72093C11.7968 0.264669 8.19692 0.275554 6.78508 2.72093L0.494796 13.616C-0.919613 16.0658 0.87246 19.1183 3.66468 19.1183C4.91898 19.1183 6.15834 18.4751 6.8465 17.2831L9.99996 11.8212L13.1534 17.2831C13.8405 18.4732 15.079 19.1183 16.3352 19.1183C19.1256 19.1183 20.9203 16.0671 19.5051 13.616ZM5.73332 16.6404C5.17811 17.6021 4.04942 18.0375 3.01014 17.7387C1.51768 17.3051 0.839521 15.5896 1.60794 14.2587C2.26606 13.1187 3.72128 12.7286 4.8615 13.3868C6.00152 14.045 6.3916 15.5003 5.73332 16.6404ZM7.127 14.2266C6.52825 12.526 4.77574 11.5127 3.00167 11.8448L6.55307 5.69363C6.7113 6.17741 6.60015 5.93247 9.25789 10.5358L7.127 14.2266ZM17.5202 17.5123C16.3803 18.1705 14.9248 17.7806 14.2666 16.6404L7.97636 5.74536C6.77605 3.66637 9.08995 1.30125 11.1518 2.4918C11.9234 2.93727 11.7111 2.68706 18.392 14.2587C19.0486 15.396 18.6575 16.8556 17.5202 17.5123Z",
				fill: color,
			}),
		),
		React.createElement(
			"defs",
			null,
			React.createElement(
				"clipPath",
				{ id: "clip_ga" },
				React.createElement("rect", {
					width: "20",
					height: "20",
					fill: "white",
				}),
			),
		),
	);

// --- Shared STUDIO_CARDS definition ---
export const STUDIO_CARDS: StudioCard[] = [
	{
		id: "s1",
		label: "Market Overview",
		icon: IconMarketOverview,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "market-overview",
	},
	{
		id: "s2",
		label: "Target Personas",
		icon: IconTargetPersonas,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "target-personas",
	},
	{
		id: "s3",
		label: "Competitors",
		icon: IconCompetitors,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "competitors",
	},
	{
		id: "s4",
		label: "Offer (Key Message)",
		icon: IconOffer,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "offer-and-key-messages",
	},
	{
		id: "s5",
		label: "Cultural Adaptation",
		icon: IconCulturalAdaptation,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "cultural-adaptation",
	},
	{
		id: "s6",
		label: "Channels Overview",
		icon: IconChannelsOverview,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "channels-overview",
	},
	{
		id: "s7",
		label: "Media Plan",
		icon: IconMediaPlan,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "media-plan-and-budget",
	},
	{
		id: "s8",
		label: "Content Plan",
		icon: IconContentPlan,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: "content-plan",
	},
	{
		id: "s9",
		label: "Instagram Ads",
		icon: IconInstagramAds,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: null,
	},
	{
		id: "s10",
		label: "Google Ads",
		icon: IconGoogleAds,
		color: "bg-bg-surface border border-border-subtle",
		toolSlug: null,
	},
];
